// EST monthly bonus — the "EST bonus" tab beside Manager entry on the Payroll
// page (owner, 2026-10-05). Rules: payroll/decisions/LOG.md 2026-10-05.
//
// One row per esthetician for the chosen month. The five numbers come from the
// month's Meevo reports (db/load_est_bonus_inputs.js) and can be corrected
// here — a corrected number keeps the Meevo original beside it. The bonus and
// every line of it are computed by the server (src/est_bonus_calc.js); this
// page never does the math. "Add to Manager Entry" submits the bonuses as
// BONUS lines on the bonus payroll (the pay period holding the month's last
// day) through the normal Manager Entry submission and its guards.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { refetchResource, useResource } from '../lib/store';
import { session } from '../lib/session';
import {
  EST_BONUS_CLINICS, estBonusKey, fetchEstBonusMonth, lastFinishedMonth, revertEstBonusMonth, saveEstBonusMonth,
  sendEstBonusesToManagerEntry,
  type EstBonusComputed, type EstBonusInputs, type EstBonusMonth, type EstBonusPerson,
} from '../lib/estBonusApi';
import { FaultState } from './FaultState';
import { voidEntryLine } from '../lib/managerEntry';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { usDate } from '../lib/payPeriodLabel';

/** $1,234.56 — bonus money is always shown to the cent. */
function formatMoney(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** "09/2026" — dates are numbers on every page (design law, INV-8). */
function mmYyyy(year: number, month: number): string {
  return `${String(month).padStart(2, '0')}/${year}`;
}

const FIELDS: Array<{ key: keyof EstBonusInputs; label: string; money?: boolean }> = [
  { key: 'serviceHours', label: 'Service hours' },
  { key: 'serviceCount', label: 'Services' },
  { key: 'ascCount', label: 'Advanced skincare' },
  { key: 'enhancementCount', label: 'Enhancements' },
  { key: 'retailNet', label: 'Retail $ (after refunds)', money: true },
];

/** Months from the clinic's plan start to the last finished month, newest first. */
export function monthsFor(location: string, today = new Date()): Array<{ year: number; month: number }> {
  const plan = EST_BONUS_CLINICS[location];
  if (!plan) return [];
  const last = lastFinishedMonth(today);
  const out: Array<{ year: number; month: number }> = [];
  let y = last.year;
  let m = last.month;
  while (y > plan.startYear || (y === plan.startYear && m >= plan.startMonth)) {
    out.push({ year: y, month: m });
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return out;
}

function pct(v: number | undefined): string {
  return v === undefined ? '—' : `${(v * 100).toFixed(1)}%`;
}

function show(v: number | null, money = false): string {
  if (v === null || v === undefined) return '—';
  return money ? formatMoney(v) : String(v);
}

/** "Wk 37: 46.5 h · Wk 39: 39 h" — the two paychecks full time is judged on. */
export function paycheckText(t: EstBonusComputed['fullTimeTest']): string {
  if (!t || !t.paychecks.length) return 'no earlier paychecks';
  return t.paychecks.map((pc) => `Wk ${pc.adpWeek}: ${pc.hours === null ? 'not loaded' : `${pc.hours} h`}`).join(' · ');
}

/** Who still needs to go to Manager Entry: computed, above zero, not history, not sent. */
export function sendable(people: EstBonusPerson[]): EstBonusPerson[] {
  return people.filter((p) => p.source !== 'SHEET' && !p.sent
    && p.computed.ready && (p.computed.total ?? 0) > 0);
}

/** Where this row stands with payroll — and the one button to move it on
 * (owner, 2026-10-06: "the actual final bonus item ... some sort of a submit
 * to manager entry button, which basically tells it that it's ready"). */
export type RowPayrollState = 'history' | 'missing' | 'none' | 'ready' | 'onPayroll' | 'changed' | 'remove';

export function rowPayrollState(p: EstBonusPerson): RowPayrollState {
  const c = p.computed;
  if (p.source === 'SHEET') return 'history';
  if (p.sent) {
    if (!c.ready) return 'onPayroll';
    const total = c.total ?? 0;
    if (Math.abs(total - p.sent.amount) < 0.005) return 'onPayroll';
    return total > 0 ? 'changed' : 'remove';
  }
  if (!c.ready) return 'missing';
  return (c.total ?? 0) > 0 ? 'ready' : 'none';
}

function RowAction({
  month, p, isOwner, onDone,
}: { month: EstBonusMonth; p: EstBonusPerson; isOwner: boolean; onDone: (msg: string) => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [key] = useState(() => crypto.randomUUID());
  const state = rowPayrollState(p);
  const closed = !month.bonusPayroll || month.bonusPayroll.closed;
  const wk = month.bonusPayroll?.adpWeek;

  async function run(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if ((state === 'changed' || state === 'remove') && p.sent) {
        // Replace the line: void the old one, then (unless the bonus is now
        // $0) submit the new amount — both through Manager Entry's own routes.
        await voidEntryLine('earnings', p.sent.lineId, `${key}:void`);
      }
      if (state === 'ready' || state === 'changed') {
        await sendEstBonusesToManagerEntry(month, [p], `${key}:submit`);
      }
      onDone(state === 'remove'
        ? `Removed ${p.name}'s bonus from Manager Entry (Week ${wk}).`
        : `${p.name}: ${formatMoney(p.computed.total ?? 0)} ${state === 'changed' ? 'updated on' : 'added to'} Manager Entry for Week ${wk}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  let content: JSX.Element;
  if (state === 'history') content = <span class="muted">Calc sheet: {formatMoney(p.paidAmount ?? 0)}</span>;
  else if (state === 'missing') content = <span class="badge warn">Missing {p.computed.missing.join(', ')}</span>;
  else if (state === 'none') content = <span class="muted">No bonus this month</span>;
  else if (state === 'onPayroll') content = <span class="badge ok">On Manager Entry {formatMoney(p.sent?.amount ?? 0)}</span>;
  else if (!isOwner) {
    // Only the owner sends EST bonuses to Manager Entry (owner, 2026-10-06);
    // everyone else sees where the row stands.
    content = (
      <span class="badge" data-testid={`est-row-waiting-${p.employeeId}`}>
        {state === 'ready' ? 'Ready — Tomer adds it to Manager Entry'
          : state === 'changed' ? `Changed since it went on Manager Entry (${formatMoney(p.sent?.amount ?? 0)}) — Tomer updates it`
            : `Now $0 — Tomer removes it from Manager Entry (${formatMoney(p.sent?.amount ?? 0)})`}
      </span>
    );
  } else {
    const label = state === 'ready' ? 'Submit to Manager Entry'
      : state === 'changed' ? `Update Manager Entry (${formatMoney(p.sent?.amount ?? 0)} to ${formatMoney(p.computed.total ?? 0)})`
        : `Remove from Manager Entry (${formatMoney(p.sent?.amount ?? 0)})`;
    content = (
      <button type="button" class="btn primary" disabled={busy || closed} onClick={() => { void run(); }} data-testid={`est-row-send-${p.employeeId}`}>
        {busy ? 'Working…' : label}
      </button>
    );
  }
  return (
    <>
      {content}
      {closed && (state === 'ready' || state === 'changed' || state === 'remove') ? <span class="muted"> (Week {wk ?? '—'} is closed)</span> : null}
      {error ? <p class="warn" role="alert">{error}</p> : null}
    </>
  );
}

/** "Undo edit" (N-071, owner 2026-10-11): an edited (MANUAL) row with the
 * Meevo numbers kept beside it goes back to them. The row is never deleted;
 * a one-step confirm, the same as every Delete on Tasks and People. */
export function UndoEdit({
  month, p, onDone,
}: { month: EstBonusMonth; p: EstBonusPerson; onDone: (msg: string) => void }): JSX.Element | null {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [key] = useState(() => crypto.randomUUID());
  if (p.source !== 'MANUAL' || !p.meevoValues) return null;
  async function run(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await revertEstBonusMonth({
        locationCode: month.locationCode, employeeId: p.employeeId, year: month.year, month: month.month,
      }, key);
      setConfirming(false);
      onDone(`${p.name}: back to the Meevo numbers.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      {confirming ? (
        <span data-testid={`est-undo-confirm-${p.employeeId}`}>
          {' '}Go back to the Meevo numbers?{' '}
          <button type="button" class="btn" disabled={busy} onClick={() => { void run(); }} data-testid={`est-undo-yes-${p.employeeId}`}>
            {busy ? 'Working…' : 'Yes'}
          </button>{' '}
          <button type="button" class="btn" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
        </span>
      ) : (
        <button type="button" class="btn" onClick={() => setConfirming(true)} data-testid={`est-undo-${p.employeeId}`}>
          Undo edit
        </button>
      )}
      {error ? <p class="warn" role="alert">{error}</p> : null}
    </>
  );
}

function EditRow({
  month, p, isAdmin, onDone,
}: { month: EstBonusMonth; p: EstBonusPerson; isAdmin: boolean; onDone: () => void }): JSX.Element {
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries(
    FIELDS.map((f) => [f.key, p.inputs[f.key] === null ? '' : String(p.inputs[f.key])]),
  ));
  const [ft, setFt] = useState<string>(p.fullTimeOverride === null ? 'test' : p.fullTimeOverride ? 'yes' : 'no');
  const [reason, setReason] = useState(p.fullTimeReason ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const numbers = Object.fromEntries(FIELDS.map((f) => [f.key, vals[f.key] === '' ? null : Number(vals[f.key])])) as unknown as EstBonusInputs;
      await saveEstBonusMonth({
        locationCode: month.locationCode, employeeId: p.employeeId, year: month.year, month: month.month,
        ...numbers,
        ...(isAdmin ? { fullTimeOverride: ft === 'test' ? null : ft === 'yes', fullTimeReason: reason } : {}),
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr class="est-edit" data-testid={`est-edit-${p.employeeId}`}>
      <td colSpan={9}>
        <div class="new-hire-block">
          {FIELDS.map((f) => (
            <label key={f.key} class="nh-field">
              <span class="nh-label">{f.label}</span>
              <input
                type="number" step="0.01" value={vals[f.key]}
                data-testid={`est-input-${f.key}`}
                onInput={(e) => setVals({ ...vals, [f.key]: (e.target as HTMLInputElement).value })}
              />
              {p.meevoValues && p.meevoValues[f.key] !== null && String(p.meevoValues[f.key]) !== vals[f.key]
                ? <span class="muted">Meevo: {show(p.meevoValues[f.key], f.money)}</span> : null}
            </label>
          ))}
          {isAdmin ? (
            <>
              <label class="nh-field">
                <span class="nh-label">Full time this month</span>
                <select value={ft} onChange={(e) => setFt((e.target as HTMLSelectElement).value)} data-testid="est-input-fulltime">
                  <option value="test">Use the 25 hours/week test</option>
                  <option value="yes">Count as full time</option>
                  <option value="no">Count as part time</option>
                </select>
              </label>
              {ft !== 'test' ? (
                <label class="nh-field">
                  <span class="nh-label">Reason</span>
                  <input type="text" value={reason} onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
                </label>
              ) : null}
            </>
          ) : null}
        </div>
        {error ? <p class="warn" role="alert">{error}</p> : null}
        <div class="actions">
          <button type="button" class="btn primary" disabled={busy} onClick={() => { void save(); }} data-testid="est-save">
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" class="btn" onClick={onDone}>Cancel</button>
        </div>
      </td>
    </tr>
  );
}

/** One esthetician's whole calculation, laid out like the calc sheet: the
 * three percentages, then every tier with its test, its math and whether it
 * was reached, then the full-time check and the total (owner, 2026-10-05: "I
 * need to be able to see the calculations"). The numbers are the server's
 * (src/est_bonus_calc.js); nothing here recomputes them. */
export function Calculation(
  { p, c, sheet }: { p: EstBonusPerson; c: EstBonusComputed; sheet: boolean },
): JSX.Element {
  if (!c.ready) {
    // Nothing to multiply yet — say exactly what is missing, and show the
    // full-time check, which the paychecks already answer.
    const t = c.fullTimeTest;
    return (
      <div class="est-calc" data-testid={`est-calc-${p.employeeId}`}>
        <p>
          <b>Waiting for:</b> {c.missing.join(', ')}.
          {c.missing.some((m) => m !== 'service hours on the last two paychecks')
            ? ' These come from the month\'s Meevo reports, or press Edit to enter them.' : ''}
        </p>
        {t ? (
          <p data-testid={`est-calc-fulltime-${p.employeeId}`} class={c.fullTime === false ? 'bad' : ''}>
            <b>Full time:</b> {paycheckText(t)} — needs {t.needs} service hours on each of the last two paychecks
            {': '}
            {c.fullTime === null ? 'not known yet'
              : c.fullTime ? (c.fullTimeOnHold && p.fullTimeOverride === null && t.short.length ? 'requirement on hold (already on the program)' : 'qualified')
                : `not qualified${t.short.length ? ` (short on Wk ${t.short.join(', Wk ')})` : ''} — no bonus above base this month`}
            {p.fullTimeOverride !== null ? ` (set by owner: ${p.fullTimeReason})` : ''}
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <div class="est-calc" data-testid={`est-calc-${p.employeeId}`}>
      <p>
        {sheet ? <b>From the calc sheet's numbers: </b> : null}
        Advanced skincare <b>{pct(c.rates?.ascPct)}</b>
        {' · '}enhancement attachment <b>{pct(c.rates?.enhPct)}</b>
        {' · '}retail <b>{formatMoney(c.rates?.retailAvg ?? 0)}</b> per service
        {c.retailMinimumMet === false ? ' (under the $1,500 retail minimum)' : ''}
      </p>
      <table class="data" data-testid={`est-calc-table-${p.employeeId}`}>
        <thead>
          <tr>
            <th class="name">Bonus tier</th>
            <th>Test</th>
            <th>Math</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {c.lines.map((l) => (
            <tr key={l.key} class={l.earned ? '' : 'muted'} data-testid={`est-calc-line-${l.key}`}>
              <td class="name">{l.label}</td>
              <td>{l.test ?? ''}</td>
              <td>{l.earned ? l.formula : 'not reached'}</td>
              <td>{l.earned ? formatMoney(l.amount) : '$0.00'}</td>
            </tr>
          ))}
          <tr>
            <td class="name"><b>Earned</b></td>
            <td />
            <td />
            <td><b>{formatMoney(c.earnedBeforeFullTime ?? 0)}</b></td>
          </tr>
          {sheet ? null : (
            <tr data-testid={`est-calc-fulltime-${p.employeeId}`}>
              <td class="name">Full time</td>
              <td>
                {c.fullTimeTest ? `${paycheckText(c.fullTimeTest)} — needs ${c.fullTimeTest.needs} service hours on each of the last two paychecks` : ''}
                {p.fullTimeOverride !== null ? ` (set by owner: ${p.fullTimeReason})` : ''}
              </td>
              <td>
                {c.fullTime
                  ? (c.fullTimeOnHold && p.fullTimeOverride === null && c.fullTimeTest?.short.length
                    ? 'requirement on hold (already on the program) — paid'
                    : 'qualified — paid')
                  : `not qualified${c.fullTimeTest?.short.length ? ` (short on Wk ${c.fullTimeTest.short.join(', Wk ')})` : ''} — $0 above base`}
              </td>
              <td><b>{formatMoney(c.total ?? 0)}</b></td>
            </tr>
          )}
          {sheet ? (
            <tr data-testid={`est-calc-sheet-${p.employeeId}`}>
              <td class="name">Calc sheet paid</td>
              <td>{Math.abs((p.paidAmount ?? 0) - (c.earnedBeforeFullTime ?? 0)) < 0.01 ? 'matches' : 'the sheet did not check the $1,500 retail minimum'}</td>
              <td />
              <td><b>{formatMoney(p.paidAmount ?? 0)}</b></td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {p.sourceRef ? <p class="muted">From: {p.sourceRef}</p> : null}
    </div>
  );
}

/** What the plan pays, in one place above the table. */
function HowItWorks(): JSX.Element {
  return (
    <details class="est-how" open data-testid="est-how">
      <summary>How the bonus is calculated</summary>
      <ul>
        <li>Base pay already on every biweekly payroll: $8 per advanced skincare service, $2.50 per enhancement, 10% of retail. This bonus is only the top-up.</li>
        <li>Percentages are out of the month's services: advanced skincare ÷ services, enhancements ÷ services, retail ÷ services.</li>
        <li>Advanced skincare 25%+: +$4 each. 40%+: another +$4 each. 50%+: +$1 per service hour.</li>
        <li>Enhancements 30%+: +$2.50 each. 40%+: +$1 per service hour.</li>
        <li>Retail $15+ per service: +2.5% of retail. $25+: another +2.5%. $30+: +$1 per service hour. All three need at least $1,500 retail for the month.</li>
        <li>Full time only: 45+ service hours on each of the last two paychecks before the bonus payroll. Anyone short is marked "not qualified" and gets $0 above base. For now this is on hold for anyone already on the program; it applies to people new to it.</li>
        <li>Paid on the payroll that holds the month's last day.</li>
      </ul>
    </details>
  );
}

export function EstBonus({ clinic }: { clinic: string }): JSX.Element {
  const months = monthsFor(clinic);
  const [chosen, setChosen] = useState<{ year: number; month: number } | null>(null);
  const ym = chosen && months.some((m) => m.year === chosen.year && m.month === chosen.month) ? chosen : months[0];
  const key = ym ? estBonusKey(clinic, ym.year, ym.month) : null;
  const load = useResource<EstBonusMonth>(key, () => fetchEstBonusMonth(clinic, ym!.year, ym!.month));
  // Several people's Edit forms can be open at once (owner, 2026-10-05).
  const [editing, setEditing] = useState<Set<number>>(() => new Set());
  const toggleEdit = (id: number): void => {
    const next = new Set(editing);
    if (next.has(id)) next.delete(id); else next.add(id);
    setEditing(next);
  };
  const closeEdit = (id: number): void => {
    const next = new Set(editing);
    next.delete(id);
    setEditing(next);
  };
  const [showCalc, setShowCalc] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendKey, setSendKey] = useState(() => crypto.randomUUID());
  const [message, setMessage] = useState<string | null>(null);
  const role = session.value.me?.role;
  const isAdmin = role === 'OWNER' || role === 'DIRECTOR';
  // Submitting to Manager Entry is the owner's alone (2026-10-06).
  const isOwner = role === 'OWNER' && !session.value.me?.viewingAs;

  if (!ym) {
    // Lake Mary and Winter Park (owner, 2026-10-05): the tab is there, empty,
    // until the clinic joins the plan (src/est_bonus_calc.js EST_BONUS_PLANS).
    return (
      <div class="panel" data-testid="est-bonus">
        <h2>Esthetician bonus</h2>
        <p class="empty" data-testid="est-bonus-empty">
          {LOCATION_NAMES[clinic as LocationCode] ?? clinic} is not on the esthetician bonus plan yet — nothing to calculate.
        </p>
      </div>
    );
  }

  const refresh = (): void => { if (key) void refetchResource(key, () => fetchEstBonusMonth(clinic, ym.year, ym.month)); };

  if (load.status === 'loading') return <div class="panel" data-testid="est-bonus"><p class="muted">Loading.</p></div>;
  if (load.status === 'fault') return <FaultState name="est_bonus_load_failed" detail={load.error} />;
  const data = load.data;
  const toSend = sendable(data.people);
  const payroll = data.bonusPayroll;

  async function send(): Promise<void> {
    setSending(true);
    setMessage(null);
    try {
      await sendEstBonusesToManagerEntry(data, toSend, sendKey);
      setMessage(`Added ${toSend.length} bonus line(s) to Manager Entry for Week ${payroll?.adpWeek}.`);
      setSendKey(crypto.randomUUID());
      refresh();
    } catch (err) {
      setMessage(`Could not add: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSending(false);
    }
  }

  return (
    <div class="panel" data-testid="est-bonus">
      <div class="section-head-row">
        <h2>Esthetician bonus — {data.monthLabel}</h2>
        <select
          value={`${ym.year}-${ym.month}`}
          data-testid="est-month"
          onChange={(e) => {
            const [y, m] = (e.target as HTMLSelectElement).value.split('-').map(Number);
            setChosen({ year: y, month: m });
            setEditing(new Set());
          }}
        >
          {months.map((m) => <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>{mmYyyy(m.year, m.month)}</option>)}
        </select>
      </div>
      <p class="muted" data-testid="est-bonus-payroll">
        {payroll
          ? `Bonus payroll: Week ${payroll.adpWeek} (${usDate(payroll.startDate)} – ${usDate(payroll.endDate)}), the pay period holding ${usDate(payroll.monthLastDay)}${payroll.closed ? ' — closed' : ''}.`
          : 'No pay period holds the last day of this month yet.'}
        {' '}Plan: {data.plan?.name ?? '—'}.
      </p>
      <HowItWorks />
      <div class="actions">
        <button type="button" class="btn" onClick={() => setShowCalc(!showCalc)} data-testid="est-toggle-calc">
          {showCalc ? 'Hide calculations' : 'Show calculations'}
        </button>
      </div>

      <div class="table-wrap">
        <table class="data" data-testid="est-bonus-table">
          <thead>
            <tr>
              <th class="name">Esthetician</th>
              {FIELDS.map((f) => <th key={f.key}>{f.label}</th>)}
              <th>Full time</th>
              <th>Bonus</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.people.map((p) => {
              const c = p.computed;
              // Owner and director can edit every row, including one already on
              // Manager Entry (owner, 2026-10-06); a manager, only unsent ones.
              const canEdit = data.covered && (isAdmin || (p.source !== 'SHEET' && !p.sent));
              return [
                <tr key={p.employeeId} data-testid={`est-row-${p.employeeId}`}>
                  <td class="name">
                    {p.name}
                    {p.source === 'MANUAL' ? <span class="badge" title={p.updatedBy ?? ''}>Edited</span> : null}
                    {p.source === 'MEEVO' ? <span class="muted" title={p.sourceRef ?? ''}> Meevo</span> : null}
                    {canEdit ? (
                      <button type="button" class="btn" onClick={() => toggleEdit(p.employeeId)} data-testid={`est-edit-btn-${p.employeeId}`}>
                        {editing.has(p.employeeId) ? 'Close' : 'Edit'}
                      </button>
                    ) : null}
                    {canEdit ? <UndoEdit month={data} p={p} onDone={(msg) => { setMessage(msg); refresh(); }} /> : null}
                  </td>
                  {FIELDS.map((f) => <td key={f.key}>{show(p.inputs[f.key], f.money)}</td>)}
                  <td data-testid={`est-ft-${p.employeeId}`}>
                    {p.source === 'SHEET' || !c.fullTimeTest
                      ? '—'
                      : (
                        <span class={c.fullTime === false ? 'bad' : ''}>
                          {c.fullTime === null ? 'Not known yet' : c.fullTime ? 'Yes' : 'No — not qualified'}
                          {p.fullTimeOverride !== null ? ' (set by owner)'
                            : c.fullTimeOnHold ? ' (requirement on hold)' : ''}
                          <br />
                          <span class="muted">{paycheckText(c.fullTimeTest)}</span>
                        </span>
                      )}
                  </td>
                  <td>
                    {c.ready ? <b data-testid={`est-total-${p.employeeId}`}>{formatMoney(c.total ?? 0)}</b> : '—'}
                  </td>
                  <td><RowAction month={data} p={p} isOwner={isOwner} onDone={(msg) => { setMessage(msg); refresh(); }} /></td>
                </tr>,
                showCalc ? (
                  <tr key={`${p.employeeId}-detail`} class="est-detail" data-testid={`est-detail-${p.employeeId}`}>
                    <td colSpan={9}>
                      {p.sheetCalc ? <Calculation p={p} c={p.sheetCalc} sheet /> : <Calculation p={p} c={c} sheet={false} />}
                    </td>
                  </tr>
                ) : null,
                editing.has(p.employeeId) ? (
                  <EditRow key={`${p.employeeId}-edit`} month={data} p={p} isAdmin={isAdmin} onDone={() => { closeEdit(p.employeeId); refresh(); }} />
                ) : null,
              ];
            })}
          </tbody>
        </table>
      </div>

      {message ? <p class={message.startsWith('Could not') ? 'warn' : 'ok'} role="status">{message}</p> : null}
      {data.covered && isOwner ? (
        <div class="actions">
          <button
            type="button"
            class="btn primary"
            disabled={sending || toSend.length === 0 || !payroll || payroll.closed}
            onClick={() => { void send(); }}
            data-testid="est-send"
          >
            {sending ? 'Adding…' : `Add ${toSend.length} bonus${toSend.length === 1 ? '' : 'es'} to Manager Entry${payroll ? ` (Week ${payroll.adpWeek})` : ''}`}
          </button>
          <span class="muted"> Each goes on as a BONUS line: "{data.reason}".</span>
        </div>
      ) : null}
    </div>
  );
}
